import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import { feedbackToolApi } from './feedbackToolApi';
import Toast from '../../Toast';
import { toast } from 'react-toastify';

const getFilterKey = (params = {}) => [
  params.years ?? [],
  params.subjects ?? [],
  params.taskTypes ?? [],
  params.responseTypes ?? [],
  params.state,
  params.sortOrder,
];

export const QUERY_KEYS = {
  items: (params, context) => ['feedbackTool', 'items', context, getFilterKey(params)],
  itemsCount: (params, context) => ['feedbackTool', 'itemsCount', context, getFilterKey(params)],
  item: (id, context) => ['feedbackTool', 'item', context, id],
};

export function useFeedbackToolQueries(queryParams = {}, context = 'markingCriteria') {
  const queryClient = useQueryClient();
  


  const itemsQuery = useQuery({
    queryKey: QUERY_KEYS.items(queryParams, context),
    queryFn: () => {
      return feedbackToolApi.getItems(queryParams, context);
    },
  });

  


  const itemsCountQuery = useQuery({
    queryKey: QUERY_KEYS.itemsCount(queryParams, context),
    queryFn: () => feedbackToolApi.getItemsCount(queryParams, context),
  });

  const isMarkingCriteriaStructure = (data) => {
    return data && typeof data === 'object' && !Array.isArray(data) && data.markingCriterias && data.preferences;
  };

  const isCommentBanksStructure = (data) => {
    const result = data && typeof data === 'object' && !Array.isArray(data) && data.commentBanks && Array.isArray(data.commentBanks);
    return result;
  };

  const isSuccessCriteriaStructure = (data) => {
    return data && typeof data === 'object' && !Array.isArray(data) && data.successCriteria && Array.isArray(data.successCriteria);
  };

  const isShortAnswersStructure = (data) => {
    return data && typeof data === 'object' && !Array.isArray(data) && data.shortAnswers && Array.isArray(data.shortAnswers);
  };

  const hideItemsMutation = useMutation({
    mutationFn: ({ selectedfeedbacktools }) => feedbackToolApi.hideItems(selectedfeedbacktools, context),
    onMutate: async ({ selectedfeedbacktools }) => {
      
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      const previousItems = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context));
      const previousCount = queryClient.getQueryData(QUERY_KEYS.itemsCount(queryParams, context));



      queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), (old) => {
        if (!old) return old;
        
        if (isMarkingCriteriaStructure(old)) {
          const updatedPreferences = old.preferences.map(pref => {
            if (selectedfeedbacktools.includes(pref.id)) {
              return {
                ...pref,
                isVisible: false,
                visible: false
              };
            }
            return pref;
          });
          
          return {
            ...old,
            preferences: updatedPreferences
          };
        } else if (isCommentBanksStructure(old)) {

          const updatedCommentBanks = old.commentBanks.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: false,
                visible: false
              };
            }
            return item;
          });
          
          return {
            ...old,
            commentBanks: updatedCommentBanks
          };
        } else if (isSuccessCriteriaStructure(old)) {
          const updatedSuccessCriteria = old.successCriteria.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: false,
                visible: false
              };
            }
            return item;
          });

          return {
            ...old,
            successCriteria: updatedSuccessCriteria
          };
        } else if (isShortAnswersStructure(old)) {
          const updatedShortAnswers = old.shortAnswers.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: false,
                visible: false
              };
            }
            return item;
          });

          return {
            ...old,
            shortAnswers: updatedShortAnswers
          };
        } else {

          const updated = old.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: false,
                visible: false
              };
            }
            return item;
          });

          return updated;
        }
      });

      queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), (old) => {
        if (!old) return old;

        const currentData = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context)) || [];

        if (isMarkingCriteriaStructure(currentData)) {
          const visibleCount = currentData.preferences.filter(pref =>
            pref.isVisible !== false && pref.visible !== false
          ).length;

          return visibleCount;
        } else if (isCommentBanksStructure(currentData)) {
          const visibleCount = currentData.commentBanks.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else if (isSuccessCriteriaStructure(currentData)) {
          const visibleCount = currentData.successCriteria.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else if (isShortAnswersStructure(currentData)) {
          const visibleCount = currentData.shortAnswers.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else {
          const visibleCount = Array.isArray(currentData) ? currentData.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length : 0;

          return visibleCount;
        }
      });

      return { previousItems, previousCount };
    },
    onError: (err, variables, context) => {
      console.error('Error hiding items:', err);
      if (context?.previousItems) {
        queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), context.previousItems);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), context.previousCount);
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      const itemType = context === 'successCriteria' ? 'success criteria' :
                      context === 'shortAnswers' ? 'short answer' :
                      context === 'commentBanks' ? 'comment bank' :
                      'rubrics';
      toast(<Toast message={`Error hiding ${itemType}. Rolled back.`} />);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });
    },
  });

  const showItemsMutation = useMutation({
    mutationFn: ({ selectedfeedbacktools }) => feedbackToolApi.showItems(selectedfeedbacktools, context),
    onMutate: async ({ selectedfeedbacktools }) => {
      
      
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      const previousItems = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context));
      const previousCount = queryClient.getQueryData(QUERY_KEYS.itemsCount(queryParams, context));

      

      queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), (old) => {
        if (!old) return old;

        if (isMarkingCriteriaStructure(old)) {
          const updatedPreferences = old.preferences.map(pref => {
            if (selectedfeedbacktools.includes(pref.id)) {
              return {
                ...pref,
                isVisible: true,
                visible: true
              };
            }
            return pref;
          });

          return {
            ...old,
            preferences: updatedPreferences
          };
        } else if (isCommentBanksStructure(old)) {

          const updatedCommentBanks = old.commentBanks.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: true,
                visible: true
              };
            }
            return item;
          });

          return {
            ...old,
            commentBanks: updatedCommentBanks
          };
        } else if (isSuccessCriteriaStructure(old)) {
          const updatedSuccessCriteria = old.successCriteria.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: true,
                visible: true
              };
            }
            return item;
          });

          return {
            ...old,
            successCriteria: updatedSuccessCriteria
          };
        } else if (isShortAnswersStructure(old)) {
          const updatedShortAnswers = old.shortAnswers.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: true,
                visible: true
              };
            }
            return item;
          });

          return {
            ...old,
            shortAnswers: updatedShortAnswers
          };
        } else {

          const updated = old.map(item => {
            if (selectedfeedbacktools.includes(item.id)) {
              return {
                ...item,
                isVisible: true,
                visible: true
              };
            }
            return item;
          });


          return updated;
        }
      });

      queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), (old) => {
        if (!old) return old;

        const currentData = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context)) || [];

        if (isMarkingCriteriaStructure(currentData)) {
          const visibleCount = currentData.preferences.filter(pref =>
            pref.isVisible !== false && pref.visible !== false
          ).length;

          return visibleCount;
        } else if (isCommentBanksStructure(currentData)) {
          const visibleCount = currentData.commentBanks.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else if (isSuccessCriteriaStructure(currentData)) {
          const visibleCount = currentData.successCriteria.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else if (isShortAnswersStructure(currentData)) {
          const visibleCount = currentData.shortAnswers.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else {
          const visibleCount = Array.isArray(currentData) ? currentData.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length : 0;

          return visibleCount;
        }
      });

      return { previousItems, previousCount };
    },
    onError: (err, variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), context.previousItems);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), context.previousCount);
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      toast(<Toast message={'Error showing items. Rolled back.'} />);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });
    }
  });

  const moveToTopMutation = useMutation({
    mutationFn: ({ selectedfeedbacktools }) => {
      
      return feedbackToolApi.moveToTop(selectedfeedbacktools, context);
    },
    onMutate: async ({ selectedfeedbacktools }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      const previousItems = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context));
      const previousCount = queryClient.getQueryData(QUERY_KEYS.itemsCount(queryParams, context));
      

      queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), (old) => {
        if (!old) {
          
          return old;
        }

        if (isMarkingCriteriaStructure(old)) {
          const selectedPreferences = old.preferences.filter(pref => selectedfeedbacktools.includes(pref.id));
          const nonSelectedPreferences = old.preferences.filter(pref => !selectedfeedbacktools.includes(pref.id));

          const updatedSelectedPreferences = selectedPreferences.map((pref, index) => ({
            ...pref,
            sortOrder: index + 1
          }));
          
          const updatedNonSelectedPreferences = nonSelectedPreferences.map((pref, index) => ({
            ...pref,
            sortOrder: selectedPreferences.length + index + 1
          }));
          
          const result = {
            ...old,
            preferences: [...updatedSelectedPreferences, ...updatedNonSelectedPreferences]
          };

          return result;
        } else if (isCommentBanksStructure(old)) {

          const selectedItems = old.commentBanks.filter(item => selectedfeedbacktools.includes(item.id));
          const nonSelectedItems = old.commentBanks.filter(item => !selectedfeedbacktools.includes(item.id));

          const updatedSelectedItems = selectedItems.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));
          
          const updatedNonSelectedItems = nonSelectedItems.map((item, index) => ({
            ...item,
            sortOrder: selectedItems.length + index + 1
          }));
          
          const result = {
            ...old,
            commentBanks: [...updatedSelectedItems, ...updatedNonSelectedItems]
          };
          
          return result;
        } else if (isSuccessCriteriaStructure(old)) {
          const selectedItems = old.successCriteria.filter(item => selectedfeedbacktools.includes(item.id));
          const nonSelectedItems = old.successCriteria.filter(item => !selectedfeedbacktools.includes(item.id));

          const updatedSelectedItems = selectedItems.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));

          const updatedNonSelectedItems = nonSelectedItems.map((item, index) => ({
            ...item,
            sortOrder: selectedItems.length + index + 1
          }));

          const result = {
            ...old,
            successCriteria: [...updatedSelectedItems, ...updatedNonSelectedItems]
          };

          return result;
        } else if (isShortAnswersStructure(old)) {
          const selectedItems = old.shortAnswers.filter(item => selectedfeedbacktools.includes(item.id));
          const nonSelectedItems = old.shortAnswers.filter(item => !selectedfeedbacktools.includes(item.id));

          const updatedSelectedItems = selectedItems.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));

          const updatedNonSelectedItems = nonSelectedItems.map((item, index) => ({
            ...item,
            sortOrder: selectedItems.length + index + 1
          }));

          const result = {
            ...old,
            shortAnswers: [...updatedSelectedItems, ...updatedNonSelectedItems]
          };

          return result;
        } else {

          const selectedItems = old.filter(item => selectedfeedbacktools.includes(item.id));
          const nonSelectedItems = old.filter(item => !selectedfeedbacktools.includes(item.id));

          
          const updatedSelectedItems = selectedItems.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));
          
          const updatedNonSelectedItems = nonSelectedItems.map((item, index) => ({
            ...item,
            sortOrder: selectedItems.length + index + 1
          }));
          
          const result = [...updatedSelectedItems, ...updatedNonSelectedItems];
          return result;
        }
      });

      return { previousItems, previousCount };
    },
    onError: (err, variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), context.previousItems);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), context.previousCount);
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      toast(<Toast message={'Error moving items to top. Rolled back.'} />);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });
    },
  });

  const deleteItemsMutation = useMutation({
    mutationFn: ({ itemsToDelete, systemItems }) => feedbackToolApi.deleteItems({ itemsToDelete, systemItems }, context),
    onMutate: async ({ itemsToDelete, systemItems }) => {

      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      const previousItems = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context));
      const previousCount = queryClient.getQueryData(QUERY_KEYS.itemsCount(queryParams, context));

      const deletableItems = itemsToDelete.filter(id => 
        !systemItems?.some(systemItem => systemItem.id === id)
      );

      queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), (old) => {
        if (!old) return old;

        if (isMarkingCriteriaStructure(old)) {
          const filteredPreferences = old.preferences.filter(pref => !deletableItems.includes(pref.id));
          const filteredMarkingCriterias = old.markingCriterias.filter(criteria => !deletableItems.includes(criteria.id));
          
          const result = {
            ...old,
            markingCriterias: filteredMarkingCriterias,
            preferences: filteredPreferences
          };

          return result;
        } else if (isCommentBanksStructure(old)) {
          
          const filteredCommentBanks = old.commentBanks.filter(item => !deletableItems.includes(item.id));
          
          const result = {
            ...old,
            commentBanks: filteredCommentBanks
          };

          return result;
        } else if (isSuccessCriteriaStructure(old)) {
          const filteredSuccessCriteria = old.successCriteria.filter(item => !deletableItems.includes(item.id));

          const result = {
            ...old,
            successCriteria: filteredSuccessCriteria
          };

          return result;
        } else if (isShortAnswersStructure(old)) {
          const filteredShortAnswers = old.shortAnswers.filter(item => !deletableItems.includes(item.id));

          const result = {
            ...old,
            shortAnswers: filteredShortAnswers
          };

          return result;
        } else {

          const filtered = old.filter(item => !deletableItems.includes(item.id));

          return filtered;
        }
      });

      queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), (old) => {
        if (!old) return old;

        const currentData = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context)) || [];

        if (isMarkingCriteriaStructure(currentData)) {
          const visibleCount = currentData.preferences.filter(pref =>
            pref.isVisible !== false && pref.visible !== false
          ).length;

          return visibleCount;
        } else if (isCommentBanksStructure(currentData)) {
          const visibleCount = currentData.commentBanks.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else if (isSuccessCriteriaStructure(currentData)) {
          const visibleCount = currentData.successCriteria.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else if (isShortAnswersStructure(currentData)) {
          const visibleCount = currentData.shortAnswers.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length;

          return visibleCount;
        } else {
          const visibleCount = Array.isArray(currentData) ? currentData.filter(item =>
            item.isVisible !== false && item.visible !== false
          ).length : 0;

          return visibleCount;
        }
      });

      return { previousItems, previousCount, deletableItems };
    },
    onError: (err, variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), context.previousItems);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), context.previousCount);
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      toast(<Toast message={'Error deleting items. Rolled back.'} />);
    },

    onSuccess: (data, variables) => {
      const total = variables.itemsToDelete.length;
      const systemCount = variables.systemItems?.length || 0;
      const deletedCount = total - systemCount;
      const itemType = context === 'successCriteria' ? 'success criteria' :
                      context === 'commentBanks' ? 'comment bank' :
                      context === 'shortAnswers' ? 'short answer' :
                      'rubrics';

      let message = "";
      if (deletedCount > 0 && systemCount > 0) {
        message = `Deleted ${deletedCount} ${itemType}${deletedCount !== 1 ? 's' : ''} (${systemCount} template${systemCount !== 1 ? 's' : ''} cannot be deleted)`;
      } else if (deletedCount > 0) {
        message = `Deleted ${deletedCount} ${itemType}${deletedCount !== 1 ? 's' : ''} successfully`;
      } else if (systemCount > 0) {
        message = `Template ${itemType}${systemCount !== 1 ? 's' : ''} cannot be deleted`;
      }
      if (message) {
        toast(<Toast message={message} />);
      }
      
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });
    },
  });

  const reorderItemsMutation = useMutation({
    mutationFn: ({ criteriaId, newSortOrder }) => {
      return feedbackToolApi.reorderItems(criteriaId, newSortOrder, context);
    },
    onMutate: async ({ criteriaId, newSortOrder }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      const previousItems = queryClient.getQueryData(QUERY_KEYS.items(queryParams, context));
      const previousCount = queryClient.getQueryData(QUERY_KEYS.itemsCount(queryParams, context));
      queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), (old) => {
        if (!old) {
          return old;
        }

        if (isMarkingCriteriaStructure(old)) {
          const prefIndex = old.preferences.findIndex(pref => pref.id === criteriaId);
          if (prefIndex === -1) {
            
            return old;
          }

          const reorderedPreferences = arrayMove(old.preferences, prefIndex, newSortOrder - 1);

          
          const updatedPreferences = reorderedPreferences.map((pref, index) => ({
            ...pref,
            sortOrder: index + 1
          }));
          
          const result = {
            ...old,
            preferences: updatedPreferences
          };

          return result;
        } else if (isCommentBanksStructure(old)) {
          
          const itemIndex = old.commentBanks.findIndex(item => item.id === criteriaId);
          if (itemIndex === -1) {
            return old;
          }

          const targetIndex = Math.max(0, Math.min(newSortOrder - 1, old.commentBanks.length - 1));
          const reorderedCommentBanks = arrayMove(old.commentBanks, itemIndex, targetIndex);
          
          const updatedCommentBanks = reorderedCommentBanks.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));
          
          const result = {
            ...old,
            commentBanks: updatedCommentBanks
          };
          
          
          
          return result;
        } else if (isSuccessCriteriaStructure(old)) {
          const itemIndex = old.successCriteria.findIndex(item => item.id === criteriaId);
          if (itemIndex === -1) {
            return old;
          }

          const targetIndex = Math.max(0, Math.min(newSortOrder - 1, old.successCriteria.length - 1));
          const reorderedSuccessCriteria = arrayMove(old.successCriteria, itemIndex, targetIndex);

          const updatedSuccessCriteria = reorderedSuccessCriteria.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));

          const result = {
            ...old,
            successCriteria: updatedSuccessCriteria
          };

          return result;
        } else if (isShortAnswersStructure(old)) {
          const itemIndex = old.shortAnswers.findIndex(item => item.id === criteriaId);
          if (itemIndex === -1) {
            return old;
          }

          const targetIndex = Math.max(0, Math.min(newSortOrder - 1, old.shortAnswers.length - 1));
          const reorderedShortAnswers = arrayMove(old.shortAnswers, itemIndex, targetIndex);

          const updatedShortAnswers = reorderedShortAnswers.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));

          const result = {
            ...old,
            shortAnswers: updatedShortAnswers
          };

          return result;
        } else {

          const itemIndex = old.findIndex(item => item.id === criteriaId);
          if (itemIndex === -1) {

            return old;
          }

          const newItems = [...old];
          const [movedItem] = newItems.splice(itemIndex, 1);
          newItems.splice(newSortOrder - 1, 0, movedItem);

          const updatedItems = newItems.map((item, index) => ({
            ...item,
            sortOrder: index + 1
          }));

          return updatedItems;
        }
      });

      return { previousItems, previousCount };
    },
    onError: (err, variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(QUERY_KEYS.items(queryParams, context), context.previousItems);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(QUERY_KEYS.itemsCount(queryParams, context), context.previousCount);
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });

      toast(<Toast message={'Error reordering items. Rolled back.'} />);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.items(queryParams, context) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.itemsCount(queryParams, context) });
    }
  });

  return {
    queries: {
      items: itemsQuery,
      itemsCount: itemsCountQuery,
    },
    mutations: {
      hideItems: hideItemsMutation,
      showItems: showItemsMutation,
      moveToTop: moveToTopMutation,
      deleteItems: deleteItemsMutation,
      reorderItems: reorderItemsMutation,
    },
  };
}